// projectAccess — the frontend's view of a project's INTRINSIC access state, for
// NAVIGATION (where the `/` resolver lands, which projects the switcher + picker
// offer as enterable, the blocked-page reason). Advisory only; the BACKEND gate
// (core/access/requireProjectAccess) is authoritative for actual entry.
//
// **Viewer-independent by design** (a project is blocked or it isn't): a project
// is reachable unless it's suspended, terminated, or a commercial trial past its
// end. Demo `type` is NOT an access state — a demo is fully reachable (it just
// renders sample data).
//
// NOTE on sysadmins (ADR-022): a sysadmin CAN enter any project (the backend gate
// bypasses status + trial) — but that power is exercised through the platform
// portal's explicit "open" action, NOT the everyday switcher/picker/resolver,
// which treat blocked projects uniformly for everyone (click → the explanation
// page). So this mirror deliberately does NOT special-case sysadmins; it reflects
// the project, and the sysadmin's elevated access lives at the gate + the portal.

import type { AvailableProject } from '@ems-app/schemas/auth/responses';

export type ProjectAccess = 'ok' | 'suspended' | 'terminated' | 'trial-expired';

/** `now` is passed in (keeps this pure + testable; callers capture it once). */
export function projectAccess(p: AvailableProject, now: number): ProjectAccess {
  if (p.status === 'suspended') return 'suspended';
  if (p.status === 'terminated') return 'terminated';
  if (p.is_trial && p.trial_expires_at !== null && new Date(p.trial_expires_at).getTime() < now) {
    return 'trial-expired';
  }
  return 'ok';
}

export function isReachable(p: AvailableProject, now: number): boolean {
  return projectAccess(p, now) === 'ok';
}

/**
 * Whether a project appears in the switcher + select-project list. Suspended and
 * expired-trial projects DO appear (muted + a status badge; clicking routes to
 * /project-blocked) so they never silently vanish on a member. **Terminated
 * projects are hidden from navigation for everyone** — they live only in the
 * platform all-projects portal (a sysadmin reactivates / terminates there). They
 * still stay in `/me` so the resolver can land a terminated-only member on the
 * "Project terminated" page. Per-row reachability (projectAccess) then decides
 * enter-vs-explain. The rule is uniform for everyone — a sysadmin's `/me` is
 * non-terminated too (active + suspended), so they see suspended / expired-trial
 * badged exactly like a member (ADR-022); entry to a blocked project is via the
 * platform portal, not this list.
 */
export function appearsInProjectList(p: AvailableProject): boolean {
  return p.status !== 'terminated';
}

const MS_PER_DAY = 86_400_000;

/**
 * Whole days until a trial ends — ceil, so "expires in 3h" reads "1 day left".
 * **≤ 0 means already expired** (now or past). Shared by the banner and the
 * project rows (`projectRows` — the switcher and the chooser) so they never
 * diverge. For > 0 callers render "{n} days left"; for ≤ 0 they render the
 * expired label ("Trial expired" in the banner, the short "Expired" chip on the
 * row).
 */
export function trialDaysLeft(trialExpiresAt: string, now: number): number {
  return Math.ceil((new Date(trialExpiresAt).getTime() - now) / MS_PER_DAY);
}
